"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.facebookAdsLeadgenFormManagement = exports.facebookAdsLeadgenFormManagementArgsSchema = exports.FacebookAdsLeadgenFormAction = exports.facebookAdsCreateLeadgenFormResultSchema = exports.facebookAdsCreateLeadgenFormArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Create Leadgen Form ----------
var standardQuestionSchema = zod_1.z.object({
    key: zod_1.z.string().describe('Question key (standard field name)'),
    type: zod_1.z
        .enum([
        'FULL_NAME',
        'FIRST_NAME',
        'LAST_NAME',
        'EMAIL',
        'PHONE',
        'ADDRESS',
        'CITY',
        'STATE',
        'ZIP_CODE',
        'COUNTRY',
        'DOB',
        'GENDER',
        'COMPANY_NAME',
        'JOB_TITLE',
    ])
        .describe('Type of standard question/field'),
});
var customQuestionSchema = zod_1.z.object({
    key: zod_1.z.string().describe('Question key (for custom questions)'),
    type: zod_1.z.literal('CUSTOM').describe('Type: CUSTOM'),
    label: zod_1.z.string().min(1).describe('Label text for the question (REQUIRED for CUSTOM type)'),
    options: zod_1.z
        .array(zod_1.z.object({
        key: zod_1.z
            .string()
            .describe('Unique identifier for the option (e.g., "founder_ceo", "option1"). Use snake_case or lowercase format.'),
        value: zod_1.z
            .string()
            .describe('Display text shown to users (e.g., "Founder/CEO", "Option 1"). This is what users see in the form.'),
    }))
        .nullish()
        .describe('Options for dropdown/multiple choice questions. Each option MUST be an object with "key" and "value" fields. DO NOT use a plain string array. Correct: [{"key": "founder", "value": "Founder"}, {"key": "manager", "value": "Manager"}]. Incorrect: ["Founder", "Manager"]'),
});
var leadgenFormQuestionSchema = zod_1.z.discriminatedUnion('type', [
    standardQuestionSchema,
    customQuestionSchema,
]);
// Base fields common to all thank you page variants
var thankYouPageCommonFields = {
    title: zod_1.z.string().nullish().describe('Thank you page title'),
    body: zod_1.z.string().nullish().describe('Thank you page body text'),
    shortMessage: zod_1.z
        .string()
        .nullish()
        .describe('Optional short message that appears above the button'),
    buttonText: zod_1.z.string().nullish().describe('Button text (Facebook field: button_text)'),
    buttonDescription: zod_1.z
        .string()
        .nullish()
        .describe('Optional button description (Facebook field: button_description)'),
};
// Button types requiring website URL
var thankYouPageWithWebsiteSchema = zod_1.z.object(__assign(__assign({}, thankYouPageCommonFields), { buttonType: zod_1.z
        .enum(['VIEW_WEBSITE', 'DOWNLOAD', 'BOOK_ON_WEBSITE', 'PROMO_CODE'])
        .describe('Button type that requires a destination URL'), buttonUrl: zod_1.z
        .string()
        .url()
        .nullish()
        .describe('Destination URL for the thank you page button. If not provided, will automatically use the Landing Page URL from your Business Profile. If neither is available, the tool will fail with instructions to set up a landing page URL.'), businessPhoneNumber: zod_1.z
        .string()
        .nullish()
        .describe('Optional business phone number (not used for URL button types)') }));
// Button types requiring phone number
var thankYouPageWithPhoneSchema = zod_1.z.object(__assign(__assign({}, thankYouPageCommonFields), { buttonType: zod_1.z
        .enum(['CALL_BUSINESS', 'WHATSAPP'])
        .describe('Button type that requires a business phone number'), businessPhoneNumber: zod_1.z
        .string()
        .regex(/^\+\d{7,15}$/, {
        message: 'Business phone number must be in international format (e.g., +12345678901). Include country code with + prefix.',
    })
        .describe('Required business phone number in international format with country code (e.g., +12345678901)'), buttonUrl: zod_1.z
        .string()
        .url()
        .nullish()
        .describe('Optional URL (not typically used for phone button types)') }));
// Button types requiring neither URL nor phone
var thankYouPageWithoutRequirementsSchema = zod_1.z.object(__assign(__assign({}, thankYouPageCommonFields), { buttonType: zod_1.z
        .enum(['MESSAGE_BUSINESS', 'P2B_MESSENGER', 'SCHEDULE_APPOINTMENT', 'VIEW_ON_FACEBOOK', 'NONE'])
        .describe('Button type that does not require URL or phone number'), buttonUrl: zod_1.z.string().url().nullish().describe('Optional URL for these button types'), businessPhoneNumber: zod_1.z
        .string()
        .nullish()
        .describe('Optional business phone number (not required for these button types)') }));
var thankYouPageSchema = zod_1.z
    .discriminatedUnion('buttonType', [
    thankYouPageWithWebsiteSchema,
    thankYouPageWithPhoneSchema,
    thankYouPageWithoutRequirementsSchema,
])
    .describe('Optional thank you page content. buttonType is required and must be one of: VIEW_WEBSITE, DOWNLOAD, BOOK_ON_WEBSITE, PROMO_CODE, CALL_BUSINESS, WHATSAPP, MESSAGE_BUSINESS, P2B_MESSENGER, SCHEDULE_APPOINTMENT, VIEW_ON_FACEBOOK, NONE. URL buttons will use buttonUrl if provided, or automatically fall back to Landing Page URL from Business Profile. Phone buttons require businessPhoneNumber in international format.');
exports.facebookAdsCreateLeadgenFormArgsSchema = zod_1.z.object({
    pageId: zod_1.z
        .string()
        .nullish()
        .describe('Facebook Page ID to associate the form with. REQUIRED for form creation. If not provided, will attempt to use the page ID from Facebook credentials (selectedPageId). If neither is available, the workflow will fail and prompt the user to connect their Facebook Page.'),
    name: zod_1.z.string().describe('Form name'),
    locale: zod_1.z
        .string()
        .default('en_US')
        .describe('Locale code for the form (default: en_US). Examples: en_US, es_ES, fr_FR'),
    questions: zod_1.z
        .array(leadgenFormQuestionSchema)
        .min(1)
        .describe('Array of form questions/fields. Must include at least one question.'),
    privacyPolicyUrl: zod_1.z
        .string()
        .url()
        .nullish()
        .describe('Optional privacy policy URL override for the instant form. If not provided, will automatically use the Privacy Policy URL from your Business Profile. If neither is available, the tool will fail with instructions to set one up. Provide this to override your Business Profile default.'),
    followUpActionUrl: zod_1.z
        .string()
        .url()
        .nullish()
        .describe('Optional form-level follow-up URL that appears as a "view website" button after form submission (Facebook field: follow_up_action_url). If not provided, will use the Landing Page URL from your Business Profile. If neither is available, the tool will fail with instructions to set up a landing page URL.'),
    followUpActionText: zod_1.z
        .string()
        .nullish()
        .describe('Optional text for the form-level follow-up button (Facebook field: follow_up_action_text). Defaults to "Visit Website" if not provided. Example: "Visit Our Store", "Learn More".'),
    contextCard: zod_1.z
        .object({
        title: zod_1.z.string().nullish().describe('Context card title'),
        body: zod_1.z
            .union([zod_1.z.string(), zod_1.z.array(zod_1.z.string().min(1))])
            .nullish()
            .describe('Context card body text. Provide a single string or an array of bullet points. The tool converts this into the required content array.'),
        bodyList: zod_1.z
            .array(zod_1.z.string().min(1))
            .nullish()
            .describe('Optional additional bullet list content. Use when you want multiple paragraphs.'),
        style: zod_1.z
            .enum(['paragraph', 'list'])
            .nullish()
            .describe('Optional display style. Defaults to paragraph unless bullet lists are detected.'),
        buttonText: zod_1.z
            .string()
            .nullish()
            .describe('Optional button label (Facebook field: context_card.button_text)'),
        coverPhotoId: zod_1.z
            .string()
            .nullish()
            .describe('ID of an uploaded Page image to use as the cover photo (Facebook field: context_card.cover_photo_id). Upload the asset first and reference its ID.'),
    })
        .nullish()
        .describe('Optional context card to show before the form. Provide simple text and the tool will convert it into the Graph API shape.'),
    thankYouPage: thankYouPageSchema.nullish().describe('Optional custom thank you page content.'),
});
exports.facebookAdsCreateLeadgenFormResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Leadgen Form ID'),
    name: zod_1.z.string(),
    created: zod_1.z.boolean().default(true),
});
// ==========================
// Compound Tool: facebookAdsLeadgenFormManagement
// ==========================
exports.FacebookAdsLeadgenFormAction = zod_1.z.enum(['createForm']);
exports.facebookAdsLeadgenFormManagementArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({ name: zod_1.z.literal('createForm'), params: exports.facebookAdsCreateLeadgenFormArgsSchema }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var facebookAdsLeadgenFormManagementResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'createForm' }, schema: exports.facebookAdsCreateLeadgenFormResultSchema },
    ],
    defaultSchema: exports.facebookAdsCreateLeadgenFormResultSchema,
});
exports.facebookAdsLeadgenFormManagement = (0, common_1.createToolMetadata)({
    id: 'facebookAdsLeadgenFormManagement',
    displayName: 'Facebook Leadgen Form Management',
    displayDescription: 'Create Facebook Instant Forms (Lead Ads forms) - the actual form with questions that collects user information. Use this to CREATE the form itself, not the ad. Meta requires both a follow-up URL and privacy policy URL for all instant forms - if not provided, will automatically use URLs from your Business Profile (Landing Page URL and Privacy Policy URL). After creating the form, use the returned form ID with facebookAdsAdManagement to create ads that use this form. Requires a Facebook Page to be connected.',
    category: 'ads',
    color: common_1.BRAND_COLORS.META,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.META),
    icon: 'meta',
    argsSchema: exports.facebookAdsLeadgenFormManagementArgsSchema,
}, facebookAdsLeadgenFormManagementResultSchemas);
